import { ReactNode } from 'react';

import { Box, Card, Flex, Heading, List, ListIcon, ListItem, Skeleton, Tag, Text } from '@chakra-ui/react';
import { CheckedIcon, StarFullIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { Link } from '~/components/link';
import { trackEvent } from '~/features/analytics/track-event';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { shouldSeeAnnualBundlePlan } from '~/features/checkout/utils/should-see-annual-plan';
import { CouponAppliedTag } from '~/features/experiments/ab_plp_redesign_2025/components/coupon-applied-tag';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { BundlePriceListItem } from '~/features/plans/components/individual-plans-section-v2/bundle-price-list-item';
import { PlansPromotionsBanner } from '~/features/plans/components/plans-promotions-banner';
import { IndividualSignUpButton, TeamSignUpButton } from '~/features/plans/components/sign-up-button';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';

type PlanFeature = {
  label: string;
  isEmphasized?: boolean;
};

const sharedIndividualFeatures: PlanFeature[] = [{ label: 'Cancel anytime', isEmphasized: true }];

// The 3 cards are laid out as a single 3-column grid at desktop, with each card as a CSS
// subgrid spanning shared row tracks (badge, title/price, button, body). Row heights come
// from real content instead of hardcoded pixel guesses, so badge/title/price/button all
// self-align across cards even if copy length or locale changes. Description, features, and
// footnote share the single body row and flow top-packed, so cards without a footnote no
// longer reserve an empty trailing row.
const CARD_GRID_TEMPLATE_ROWS = 'repeat(4, auto)';
// Effective gap between the bundle's badge strip and its white card body. The shared row gap
// below (24px) is wider than the 8px this needs, so the badge row's negative margin trims it down.
const BADGE_TO_CARD_GAP = '8px';

export function PlansPricingHeroSection() {
  const { currency } = useCurrency();
  const { data: pricePlans } = usePricePlans(currency);
  const bundlePlanKey = shouldSeeAnnualBundlePlan(currency)
    ? billingFrequencySchema.enum.annually
    : billingFrequencySchema.enum['4month'];

  const monthlyPlan = pricePlans?.prices.monthly?.individual;
  const bundlePlan = pricePlans?.prices[bundlePlanKey]?.individual;
  const teamPlan = pricePlans?.prices.monthly?.team;

  return (
    <Box as='section' bgColor='grey.50'>
      <Box
        color='white'
        minHeight={{ base: '200px', lg: '320px' }}
        overflow='hidden'
        paddingBottom={{ base: '58px', lg: 0 }}
        paddingTop={{ base: '40px', lg: '56px' }}
        paddingX={0}
        position='relative'
        bgGradient={{
          base: 'linear(-83.47deg, #021031 28.34%, #00298C 100%)',
          lg: 'linear(180deg, #001E65 0%, #00133F 100%)',
        }}
      >
        <ContentContainer contentMaxWidth='content-max-width-md' textAlign='center'>
          <Heading
            as='h1'
            fontWeight={{ base: '600', lg: '500' }}
            letterSpacing={{ base: '-0.02em', lg: '-0.0275em' }}
            lineHeight={{ base: '1.1', lg: '1' }}
            mb={{ base: 3, lg: 4 }}
            fontSize={{
              base: '2.375rem',
              lg: '4.75rem',
            }}
          >
            Start learning today
          </Heading>
          <PlansPromotionsBanner />
        </ContentContainer>
      </Box>

      <ContentContainer
        contentMaxWidth='content-max-width-xl'
        marginTop={{ base: '-50px', lg: '-80px' }}
        pb={8}
        position='relative'
        zIndex={1}
      >
        <Box
          display={{ base: 'flex', lg: 'grid' }}
          flexDirection='column'
          gap={6}
          gridTemplateColumns={{ lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(3, 354px)' }}
          gridTemplateRows={{ lg: CARD_GRID_TEMPLATE_ROWS }}
          justifyContent={{ lg: 'center' }}
        >
          <PlanCard
            contentGap='18px'
            description='Unlimited access to hundreds of top-rated courses'
            gridColumn={1}
            horizontalPadding={7}
            title='Monthly subscription'
            buttonSlot={
              <IndividualSignUpButton
                analyticsEventName='Plans CTA Clicked'
                ariaLabel='Sign up for the Individual "Month-To-Month" Plan'
                billingFrequency={billingFrequencySchema.enum.monthly}
              />
            }
            features={[
              ...sharedIndividualFeatures,
              { label: 'Hands-on projects with expert feedback' },
              { label: 'Personalized career coaching and interview prep' },
              { label: 'Program Certificates' },
            ]}
            priceSlot={
              <MonthlyPrice
                couponApplied={monthlyPlan?.couponApplied}
                couponPercentOff={monthlyPlan?.couponPercentOff}
                displayPrice={monthlyPlan?.displayPrice}
                displayPriceUnits={monthlyPlan?.displayPriceUnits}
                strikeThroughDisplayPrice={monthlyPlan?.strikeThroughDisplayPrice}
              />
            }
          />
          <BestValuePlanCard
            billingFrequency={bundlePlanKey}
            couponApplied={bundlePlan?.couponApplied}
            couponPercentOff={bundlePlan?.couponPercentOff}
            description='The most cost-effective way to develop the skills you want'
            displayPricePerMonth={bundlePlan?.displayPricePerMonth}
            gridColumn={2}
            savingsBlurb={bundlePlan?.savingsBlurb}
            strikeThroughDisplayPricePerMonth={bundlePlan?.strikeThroughDisplayPricePerMonth}
            buttonSlot={
              <IndividualSignUpButton
                analyticsEventName='Plans CTA Clicked'
                billingFrequency={bundlePlanKey}
                colorScheme='purple'
                ariaLabel={
                  bundlePlanKey === billingFrequencySchema.enum.annually
                    ? 'Sign up for the Individual "Annual" Plan'
                    : 'Sign up for the Individual "Four Month" Plan'
                }
              />
            }
          />
          <PlanCard
            contentGap='14px'
            description='For Mid-Market Organizations'
            descriptionColor='grey.700'
            gridColumn={3}
            horizontalPadding={6}
            title='Team (2+ users)'
            buttonSlot={
              <TeamSignUpButton
                analyticsEventName='Plans CTA Clicked'
                ariaLabel='Sign up for the Team Plan'
                billingFrequency={billingFrequencySchema.enum.monthly}
              >
                Enroll Now
              </TeamSignUpButton>
            }
            features={[
              { label: 'Analytics dashboard for skill attainment' },
              { label: 'Ensure smooth onboarding for learners' },
              { label: 'Flexible invoice options' },
            ]}
            footnoteSlot={
              <Text color='grey.500' fontSize='sm'>
                Discount available for annual plan.{' '}
                <Link
                  color='grey.900'
                  fontWeight='medium'
                  href='/get-started/teams'
                  textDecoration='underline'
                  onClick={() =>
                    trackEvent({
                      name: 'Plans CTA Clicked',
                      type: 'click',
                      label: 'plans_teams_contact_clicked',
                    })
                  }
                >
                  Contact us
                </Link>{' '}
                to learn more.
              </Text>
            }
            priceSlot={
              <MonthlyPrice displayPrice={teamPlan?.displayPrice} displayPriceUnits={teamPlan?.displayPriceUnits} />
            }
          />
        </Box>
      </ContentContainer>
    </Box>
  );
}

function BestValuePlanCard({
  billingFrequency,
  buttonSlot,
  couponApplied,
  couponPercentOff,
  description,
  displayPricePerMonth,
  gridColumn,
  savingsBlurb,
  strikeThroughDisplayPricePerMonth,
}: {
  billingFrequency: BillingFrequency;
  buttonSlot: ReactNode;
  couponApplied?: boolean;
  couponPercentOff?: number | null;
  description: string;
  displayPricePerMonth?: string;
  gridColumn?: number;
  savingsBlurb?: string | null;
  strikeThroughDisplayPricePerMonth?: string | null;
}) {
  const title =
    billingFrequency === billingFrequencySchema.enum.annually ? 'Annual subscription' : '4-Month subscription';
  const bundleLabel =
    billingFrequency === billingFrequencySchema.enum.annually ? 'annual subscription' : '4-month bundle';

  return (
    <Box
      bgColor='purple.200'
      border='2px solid'
      borderColor='purple.500'
      borderRadius='10px'
      display={{ base: 'flex', lg: 'grid' }}
      flexDirection='column'
      gap={{ base: 2, lg: 6 }}
      gridColumn={{ lg: gridColumn }}
      gridRow={{ lg: '1 / 5' }}
      gridTemplateRows={{ lg: 'subgrid' }}
      overflow='hidden'
      pt={2}
      width={{ base: '100%', xl: '354px' }}
    >
      <Flex
        alignItems='center'
        gridRow={{ lg: 1 }}
        justifyContent='space-between'
        marginBottom={{ lg: `calc(${BADGE_TO_CARD_GAP} - 24px)` }}
        pl={4}
        pr={2}
      >
        <Flex alignItems='center' gap={2}>
          <StarFullIcon color='purple.700' height='18px' width='18px' aria-hidden />
          <Text color='purple.700' fontSize='1rem' fontWeight='600' whiteSpace='nowrap'>
            Best value plan
          </Text>
        </Flex>
        {!couponApplied && savingsBlurb && (
          <Tag
            bgGradient='linear(to-r, purple.500, purple.700)'
            borderRadius='4px'
            color='white'
            fontFamily='condensed'
            fontSize='0.875rem'
            fontWeight='500'
            h='28px'
            letterSpacing='0.08em'
            px={3}
            textTransform='uppercase'
          >
            {savingsBlurb}
          </Tag>
        )}
      </Flex>
      <Card
        bgColor='white'
        border='1px solid'
        borderColor='purple.300'
        borderRadius='8px'
        boxShadow='none'
        display={{ base: 'flex', lg: 'grid' }}
        flexDirection='column'
        gap={6}
        gridRow={{ lg: '2 / 5' }}
        gridTemplateRows={{ lg: 'subgrid' }}
        pb={8}
        pt={6}
        px={8}
        width='100%'
      >
        <CardBody
          buttonSlot={buttonSlot}
          contentGap='16px'
          description={description}
          descriptionColor='purple.500'
          featureIconColor='purple.500'
          features={[...sharedIndividualFeatures, { label: 'All the same great benefits as our monthly plan' }]}
          footnoteSlot={
            <List as='ol' color='grey.500' fontSize='0.625rem' lineHeight='1.4' spacing={2}>
              <ListItem>
                <Box as='sup' fontWeight='500'>
                  1
                </Box>{' '}
                Discount applies to the{' '}
                {billingFrequency === billingFrequencySchema.enum.annually ? 'first year' : 'first 4 months'} of
                membership, after which plans are converted to month-to-month.
              </ListItem>
              <BundlePriceListItem billingFrequency={billingFrequency} />
            </List>
          }
          priceSlot={
            displayPricePerMonth ? (
              <Flex flexDirection='column' gap={1}>
                {strikeThroughDisplayPricePerMonth && (
                  <StrikeThroughPrice
                    couponApplied={couponApplied}
                    couponPercentOff={couponPercentOff}
                    displayPrice={strikeThroughDisplayPricePerMonth}
                  />
                )}
                <Flex alignItems='baseline' columnGap={2} flexWrap='wrap' rowGap={1}>
                  <Flex alignItems='baseline' gap={2}>
                    <Heading as='p' color='grey.900' fontSize='2rem' fontWeight='600' lineHeight='1'>
                      {displayPricePerMonth}
                    </Heading>
                    <Text color='grey.500' fontSize='sm'>
                      /month
                    </Text>
                  </Flex>
                  <Text color='grey.900' fontSize='sm' fontWeight='500'>
                    for {bundleLabel}
                    <DisclaimerMarker label='2' size='11px' />
                  </Text>
                </Flex>
              </Flex>
            ) : (
              <Skeleton height='32px' width='220px' />
            )
          }
          title={
            <>
              {title}
              <DisclaimerMarker label='1' />
            </>
          }
          contentHasBottomBorder
        />
      </Card>
    </Box>
  );
}

function PlanCard({
  buttonSlot,
  contentGap = '16px',
  description,
  descriptionColor = 'blue.500',
  featureIconColor = 'blue.500',
  features,
  footnoteSlot,
  gridColumn,
  horizontalPadding = 7,
  priceSlot,
  title,
}: {
  buttonSlot: ReactNode;
  contentGap?: string;
  description: string;
  descriptionColor?: string;
  featureIconColor?: string;
  features: PlanFeature[];
  footnoteSlot?: ReactNode;
  gridColumn?: number;
  horizontalPadding?: number;
  priceSlot: ReactNode;
  title: ReactNode;
}) {
  return (
    <Card
      bgColor='white'
      border='1px solid'
      borderColor='grey.200'
      borderRadius='8px'
      boxShadow='none'
      display={{ base: 'flex', lg: 'grid' }}
      flexDirection='column'
      gap={6}
      gridColumn={{ lg: gridColumn }}
      gridRow={{ lg: '2 / 5' }}
      gridTemplateRows={{ lg: 'subgrid' }}
      pb={8}
      pt={6}
      px={horizontalPadding}
      width={{ base: '100%', xl: '354px' }}
    >
      <CardBody
        buttonSlot={buttonSlot}
        contentGap={contentGap}
        contentHasBottomBorder={Boolean(footnoteSlot)}
        description={description}
        descriptionColor={descriptionColor}
        featureIconColor={featureIconColor}
        features={features}
        footnoteSlot={footnoteSlot}
        priceSlot={priceSlot}
        title={title}
      />
    </Card>
  );
}

function CardBody({
  buttonSlot,
  contentGap = '16px',
  contentHasBottomBorder = false,
  description,
  descriptionColor = 'blue.500',
  featureIconColor = 'blue.500',
  features,
  footnoteSlot,
  priceSlot,
  title,
}: {
  buttonSlot: ReactNode;
  contentGap?: string;
  contentHasBottomBorder?: boolean;
  description: string;
  descriptionColor?: string;
  featureIconColor?: string;
  features: PlanFeature[];
  footnoteSlot?: ReactNode;
  priceSlot: ReactNode;
  title: ReactNode;
}) {
  return (
    <>
      <Box gridRow={{ lg: 1 }}>
        <Heading as='h2' fontSize='1.5rem' fontWeight='600' letterSpacing='-0.01em' lineHeight='1.15' mb={4}>
          {title}
        </Heading>
        {priceSlot}
      </Box>
      <Box gridRow={{ lg: 2 }}>{buttonSlot}</Box>
      {/* Body (row 3): description, features, and footnote flow top-packed. The row is shared
          across cards so its height equals the tallest body; shorter cards keep their content
          hugged under the button with any slack falling at the very bottom. */}
      <Flex alignSelf={{ lg: 'start' }} flexDirection='column' gap={6} gridRow={{ lg: 3 }}>
        <Flex
          borderBottomWidth={contentHasBottomBorder ? '1px' : '0'}
          borderColor='grey.200'
          borderStyle='solid'
          borderTopWidth='1px'
          flexDirection='column'
          gap={contentGap}
          pb={contentHasBottomBorder ? 6 : 0}
          pt={6}
        >
          <Text color={descriptionColor} fontSize='1rem' fontWeight='600' lineHeight='1.5'>
            {description}
          </Text>
          <FeatureList features={features} gap={contentGap} iconColor={featureIconColor} />
        </Flex>
        {footnoteSlot}
      </Flex>
    </>
  );
}

function DisclaimerMarker({ label, size = '13px' }: { label: string; size?: string }) {
  return (
    <Box
      alignItems='center'
      as='sup'
      border='1px solid'
      borderColor='grey.700'
      borderRadius='full'
      boxSize={size}
      color='grey.700'
      display='inline-flex'
      fontSize='0.5rem'
      fontWeight='700'
      justifyContent='center'
      lineHeight='1'
      ml='3px'
      verticalAlign='super'
    >
      {label}
    </Box>
  );
}

function FeatureList({
  features,
  gap = '16px',
  iconColor = 'blue.500',
}: {
  features: PlanFeature[];
  gap?: string;
  iconColor?: string;
}) {
  return (
    <List display='flex' flexDirection='column' gap={gap}>
      {features.map((feature) => (
        <ListItem key={feature.label} display='flex' gap={3}>
          <ListIcon as={CheckedIcon} color={iconColor} flexShrink={0} height='18px' mt='3px' width='18px' />
          <Text color='grey.900' fontSize='1rem' fontWeight={feature.isEmphasized ? '600' : '400'} lineHeight='1.5'>
            {feature.label}
          </Text>
        </ListItem>
      ))}
    </List>
  );
}

function MonthlyPrice({
  couponApplied,
  couponPercentOff,
  displayPrice,
  displayPriceUnits,
  strikeThroughDisplayPrice,
}: {
  couponApplied?: boolean;
  couponPercentOff?: number | null;
  displayPrice?: string;
  displayPriceUnits?: string;
  strikeThroughDisplayPrice?: string | null;
}) {
  if (!displayPrice) {
    return <Skeleton height='32px' width='150px' />;
  }

  return (
    <Flex flexDirection='column' gap={1}>
      {strikeThroughDisplayPrice && (
        <StrikeThroughPrice
          couponApplied={couponApplied}
          couponPercentOff={couponPercentOff}
          displayPrice={strikeThroughDisplayPrice}
        />
      )}
      <Flex alignItems='baseline' gap={2}>
        <Heading as='p' color='grey.900' fontSize='2rem' fontWeight='600' lineHeight='1'>
          {displayPrice}
        </Heading>
        <Text color='grey.500' fontSize='sm'>
          {displayPriceUnits}
        </Text>
      </Flex>
    </Flex>
  );
}

function StrikeThroughPrice({
  couponApplied,
  couponPercentOff,
  displayPrice,
}: {
  couponApplied?: boolean;
  couponPercentOff?: number | null;
  displayPrice: string;
}) {
  return (
    <Flex alignItems='center' gap={2}>
      <Text
        as='del'
        color='grey.500'
        fontSize='0.875rem'
        position='relative'
        textDecoration='none'
        sx={{
          '&::after': {
            content: '""',
            backgroundColor: 'grey.500',
            width: '100%',
            position: 'absolute',
            height: '1px',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%) rotate(-10deg)',
          },
        }}
      >
        {displayPrice}
      </Text>
      {couponApplied && typeof couponPercentOff === 'number' && <CouponAppliedTag value={couponPercentOff} />}
    </Flex>
  );
}
